import React, { useEffect, useMemo, useRef, useState } from "react";

// import TablePagination from "@material-ui/core/TablePagination";
import AddIcon from "@material-ui/icons/Add";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { SITE_ASSESSMENT_TYPES } from "./table.utils";
import TableHeader from "./table.header";
import TableRow from "./table.row";
import { DATA_TABLE_NAME } from "../constants";
import { isNumberNotEmpty } from "../../utils/helpers";
import { Accordion, WarningDialog } from "../../../../app/components";
import TextButton from "../../../../app/components/buttons/text";
import { getScheduleOfEventsAssessmentTableDefaults } from "../../../../app/contexts/helpers";
import { useScheduleEvents } from "../../../../app/contexts/schedule.events.context";
import { useUser } from "../../../../app/contexts/user.context";
import { BUTTON_LABELS, SOE_DELETE_WARNING_MESSAGE } from "../../../../app/components/warning.dialog/messages";

// const ROWS_PER_PAGE_KEY = "ctpo_SOE_assessments_table_rows_per_page";
// const defaultRowsPerPage = () => localStorage.getItem(ROWS_PER_PAGE_KEY)?.toString() || 5;

/*
  This component is responsible for rendering the table of assessments
  It uses the schedule events context to handle the schedule of events.
  It uses the study context to handle the study plan.
  
  Props:
  - value: number - controls the display mode (0 for edit mode, 1 for view mode)
  - country: object - contains country information including siteGroupId
  - fieldType: string - determines the type of comments field
  - siteId: string - the ID of the current site
  - setTableVisibility: function - controls table visibility
  - tableVisibility: object - current visibility state
*/
export default function AssessmentsTable({
  value,
  country,
  fieldType,
  siteId,
  setTableVisibility,
  tableVisibility,
}) {
  const { id, scheduleId } = useParams();
  const {
    currentSOE,
    scheduleOfEventsAssessments,
    setScheduleOfEventsAssessmentsChangeLog,
    scheduleOfEventsAssessmentsChangeLog,
    setScheduleOfEventsAssessments,
    setVisitsAndAssessmentsMappingChangeLog,
    fetchSOEAssessments,
    loadingCountrySOE,
    assessmentNames,
    allAssessmentNames,
  } = useScheduleEvents();
  const { isReadOnlyUser } = useUser();
  
  // Ref to track data fetching to prevent multiple calls
  const fetchingRef = useRef(new Set());
  
  // const [page, setPage] = useState(0);
  // const [filters, setFilters] = useState({});
  // const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage);

  const groupKey = (country.siteGroupId && `${country.siteGroupId}-${siteId}`) || "study";
  const [showWarning, setShowWarning] = useState(false);
  const [elementToDelete, setElementToDelete] = useState(null);
  // const allAssessments = scheduleOfEventsAssessments[groupKey] || [];

  // const filteredAssessments = useMemo(
  //   () => filterAssessments(allAssessments, filters),
  //   [allAssessments, filters]
  // );

  // const paginatedAssessments = useMemo(
  //   () => filteredAssessments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
  //   [filteredAssessments, page, rowsPerPage]
  // );

  // Create a consistent key for accessing data
  const dataKey = useMemo(() => {
    if (country.siteGroupId && siteId) {
      return `${country.siteGroupId}-${siteId}`;
    }
    return country.siteGroupId || "study";
  }, [country.siteGroupId, siteId]);

  // Determine if we're in country view
  const isCountry = useMemo(() => {
    return Boolean(country.siteGroupId);
  }, [country.siteGroupId]);

  // Calculate column count based on view type
  const columnCount = useMemo(() => (isCountry ? 5 : 14.5), [isCountry]);

  // Calculate remove column width
  const removeColWidth = value === 0 ? 5 : 0;
  const typeColumnWidth = 22;

  // /**
  //  * Pagination
  //  */
  // const handleChangePage = (event, newPage) => {
  //   setPage(newPage);
  // };

  // const handleChangeRowsPerPage = (event) => {
  //   const { value } = event.target;
  //   const rows = parseInt(value);

  //   setRowsPerPage(rows);
  //   setPage(0);
  // };

  useEffect(() => {
    const shouldFetchCountryData = isCountry && !scheduleOfEventsAssessments[dataKey];
    const shouldFetchStudyData = !isCountry && 
      scheduleOfEventsAssessments.study === undefined && 
      scheduleId && 
      id;
    
    if (shouldFetchCountryData && !fetchingRef.current.has(dataKey)) {
      fetchingRef.current.add(dataKey);
      
      (async () => {
        try {
          await fetchSOEAssessments(
            scheduleId,
            id,
            isCountry,
            country.siteGroupId,
            siteId,
          );
        } finally {
          fetchingRef.current.delete(dataKey);
        }
      })();
    }
    
    if (shouldFetchStudyData && !fetchingRef.current.has('study')) {
      fetchingRef.current.add('study');
      
      (async () => {
        try {
          await fetchSOEAssessments(
            scheduleId,
            id,
            false,
            null,
            1,
            true,
          );
        } finally {
          fetchingRef.current.delete('study');
        }
      })();
    }
  }, [
    dataKey,
    isCountry,
    siteId,
    scheduleId,
    id,
    country.siteGroupId,
    scheduleOfEventsAssessments,
    fetchSOEAssessments,
  ]);

  const tableContainerRef = React.useRef(null);

  const addNewRow = () => {
    const newData = {
      ...scheduleOfEventsAssessments,
      [dataKey]: [
        ...(scheduleOfEventsAssessments[dataKey] || []),
        getScheduleOfEventsAssessmentTableDefaults(
          parseInt(scheduleId || "0"),
          id,
          { assessmentNames: allAssessmentNames },
        ),
      ],
    };

    setScheduleOfEventsAssessments(newData);
    setScheduleOfEventsAssessmentsChangeLog({
      ...scheduleOfEventsAssessmentsChangeLog,
      [dataKey]: { someValue: "some change" },
    });
    setVisitsAndAssessmentsMappingChangeLog({ someValue: "some change" });

    setTimeout(() => {
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTo({ top: tableContainerRef.current.scrollHeight, behavior: "smooth" });
      }
    }, 200);
  };

  const removeRow = (identifier) => {
      // Find the assessment to get its name for the warning message
      const assessmentToDelete = scheduleOfEventsAssessments[country.siteGroupId || "study"]?.find(
        (sv) => (isNumberNotEmpty(sv.scheduleOfEventsAssessmentId) ? sv.scheduleOfEventsAssessmentId : sv.key) === identifier
      );

      // Store the identifier and assessment name for later deletion
      setElementToDelete({
        identifier,
        name: assessmentToDelete?.assessmentName || "this assessment"
      });
      
      // Show warning dialog
      setShowWarning(true);
  };

  const performDelete = (identifier) => {
    const filteredData = scheduleOfEventsAssessments[dataKey]?.filter(
      (sv) =>
        (isNumberNotEmpty(sv.scheduleOfEventsAssessmentId)
          ? sv.scheduleOfEventsAssessmentId
          : sv.key) !== identifier,
    );

    setScheduleOfEventsAssessments({
      ...scheduleOfEventsAssessments,
      [dataKey]: filteredData,
    });

    setScheduleOfEventsAssessmentsChangeLog({
      ...scheduleOfEventsAssessmentsChangeLog,
      [dataKey]: { someValue: "some change" },
    });
    setVisitsAndAssessmentsMappingChangeLog({ someValue: "some change" });
  };

  const handleAssessmentsValueChange = (nameValue, key) => {
    const currentData = scheduleOfEventsAssessments[dataKey] || [];

    const newData = currentData.map((sv) => {
      if ((sv.scheduleOfEventsAssessmentId || sv.key) !== key) return sv;

      let updatedRecord = { ...sv };

      Object.entries(nameValue).forEach(([name, value]) => {
        if (name === "assessmentType") {
          const filteredNames = assessmentNames
            .filter((an) =>
              SITE_ASSESSMENT_TYPES.includes(value)
                ? an.assessmentType === "Site Costs"
                : an.assessmentType === value,
            )
            .map((a) => a.assessmentName);

          updatedRecord = {
            ...updatedRecord,
            assessmentType: value,
            assessmentNames: filteredNames,
            assessmentNameMapping: "",
          };
        } else {
          updatedRecord[name] = value;
        }
      });

      return updatedRecord;
    });

    if (JSON.stringify(currentData) !== JSON.stringify(newData)) {
      setScheduleOfEventsAssessments({
        ...scheduleOfEventsAssessments,
        [dataKey]: newData,
      });
      
      const hasNonLoadChanges = Object.keys(nameValue).some(name => name !== "load");
      if (hasNonLoadChanges) {
        setScheduleOfEventsAssessmentsChangeLog({
          ...scheduleOfEventsAssessmentsChangeLog,
          [dataKey]: { someValue: "some change" },
        });
      }
    }
  };

  const onAssessmentsChange = (name, value, key) => {
    const nameValue = typeof name === 'object' ? name : { [name]: value };
    const recordKey = typeof name === 'object' ? value : key;
    handleAssessmentsValueChange(nameValue, recordKey);
  };

  const renderTableRows = () => {
    const assessments = scheduleOfEventsAssessments[groupKey] || [];
    return assessments.map((sv, index) => (
      <TableRow
        fieldType={fieldType}
        removeColWidth={removeColWidth}
        typeColumnWidth={typeColumnWidth}
        key={`${sv.key || sv.scheduleOfEventsAssessmentId}-${index}`}
        sv={{ ...sv }}
        index={index}
        removeRow={removeRow}
        onAssessmentsChange={onAssessmentsChange}
        value={value}
        assessmentNames={sv.assessmentNames}
        assessments={scheduleOfEventsAssessments[dataKey]}
        columnCount={columnCount}
        currency={isCountry ? sv?.currency : currentSOE?.currency}
        readOnly={isReadOnlyUser}
      />
    ));
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const currentData = [...(scheduleOfEventsAssessments[dataKey] || [])];
    const [removed] = currentData.splice(result.source.index, 1);
    currentData.splice(result.destination.index, 0, removed);

    setScheduleOfEventsAssessments({
      ...scheduleOfEventsAssessments,
      [dataKey]: currentData,
    });

    setScheduleOfEventsAssessmentsChangeLog({
      ...scheduleOfEventsAssessmentsChangeLog,
      [dataKey]: { someValue: "some change" },
    });
  };

  // function filterAssessments(assessments, filters) {
  //   if (!filters || Object.keys(filters).length === 0) return assessments;
  //   return assessments.filter((assessment) =>
  //     Object.entries(filters).every(([key, filter]) => {
  //       if (!filter.value) return true;
  //       const fieldValue = (assessment[key] || "").toString().toLowerCase();
  //       return fieldValue.includes(filter.value.toLowerCase());
  //     })
  //   );
  // }

  const handleWarningConfirm = () => {
    if (elementToDelete && elementToDelete.identifier) {
      performDelete(elementToDelete.identifier);
    }
    setElementToDelete(null);
    setShowWarning(false);
  };

  const handleWarningCancel = () => {
    setElementToDelete(null);
    setShowWarning(false);
  };

  return (
    <>
      {!loadingCountrySOE && (
        <Accordion
          scrollIntoView={true}
          width="100%"
          summary="Assessments"
          defaultExpanded={tableVisibility.showAssessmentsTable}
          onClick={() => setTableVisibility("showAssessmentsTable")}
          data-table-name={DATA_TABLE_NAME.ASSESSMENTS_TABLE}
        >
          {tableVisibility.showAssessmentsTable && (
            <Container
              noBottom={isCountry}
              key={dataKey}
              onClick={(event) => event.stopPropagation()}
              data-table-name={DATA_TABLE_NAME.ASSESSMENTS_TABLE}
            >
              {!isCountry ? (
                <DragDropContext onDragEnd={onDragEnd}>
                  <Table
                    data-table-container="true"
                    data-table-name={DATA_TABLE_NAME.ASSESSMENTS_TABLE}
                    ref={tableContainerRef}
                  >
                    <TableHeader
                      value={value}
                      columnCount={columnCount}
                      removeColWidth={removeColWidth}
                      typeColumnWidth={typeColumnWidth}
                      fieldType={fieldType}
                      readOnly={isReadOnlyUser}
                    />
                    <Droppable droppableId="assessments">
                      {(provided) => (
                        <RowsContainer
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {renderTableRows()}
                          {provided.placeholder}
                        </RowsContainer>
                      )}
                    </Droppable>
                  </Table>
                </DragDropContext>
              ) : (
                <Table
                  data-table-container="true"
                  data-table-name={DATA_TABLE_NAME.ASSESSMENTS_TABLE}
                  ref={tableContainerRef}
                >
                  <TableHeader
                    columnCount={columnCount}
                    value={value}
                    removeColWidth={removeColWidth}
                    typeColumnWidth={typeColumnWidth}
                    readOnly={isReadOnlyUser}
                  />
                  {renderTableRows()}
                </Table>
              )}
              <FlexRow>
                {(!isReadOnlyUser && value === 0) && (
                  <ButtonContainer>
                    <TextButton
                      sx={{ width: "4rem" }}
                      onClick={addNewRow}
                      startIcon={<AddIcon />}
                    >
                      Add Row
                    </TextButton>
                  </ButtonContainer>
                )}
                {/* <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  rowsPerPage={rowsPerPage}
                  page={page}
                  count={filteredAssessments.length}
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                /> */}
              </FlexRow>
            </Container>
          )}
                
          <WarningDialog
            open={showWarning}
            onConfirm={handleWarningConfirm}
            onCancel={handleWarningCancel}
            message={
              SOE_DELETE_WARNING_MESSAGE(elementToDelete?.name, "assessment")
            }
            confirmLabel={BUTTON_LABELS.DELETE}
          />
        </Accordion>
      )}
    </>
  );
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 1px solid var(--surface-300);
  border-top: none;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  border-bottom: ${(p) =>
    p.noBottom ? "none" : "1px solid var(--surface-300)"};
`;

const ButtonContainer = styled.div`
  margin: 1rem 0 1rem 0.4rem;
`;

const Table = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid var(--surface-300);
  width: 100%;
  max-height: 600px;
  overflow-y: auto;
  position: relative;
  &:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

const RowsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const FlexRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;
